/**
 * Profile Pop-Out Panel Content
 * 
 * User account settings and logout.
 * Contains:
 * - User Profile
 * - Billing & Subscription
 * - Dark Mode toggle
 * - Logout
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faCreditCard,
    faSignOutAlt,
    faMoon,
    faSun,
    faLifeRing,
} from '@fortawesome/free-solid-svg-icons';
import { useLogout } from '../../../hooks/api/useUser';
import { useUserProfile } from '../../../contexts/user/hooks';
import { useDarkMode } from '../../../hooks/useDarkMode';

interface ProfilePopOutProps {
    onClose: () => void;
}

export const ProfilePopOut: React.FC<ProfilePopOutProps> = ({ onClose }) => {
    const navigate = useNavigate();
    const logoutMutation = useLogout();
    const { profile } = useUserProfile();
    const { dark, toggleDarkMode } = useDarkMode();

    const handleLogout = async () => {
        try {
            await logoutMutation.mutateAsync();
        } catch {
            // Even if API call fails, we should still log out locally
        }
        // Always clear token and redirect
        localStorage.removeItem('authToken');
        navigate('/login');
        onClose();
    };

    return (
        <div className="p-4 space-y-4">
            {/* User Info */}
            {profile && (
                <div className="pb-3 border-b border-gray-700">
                    <p className="text-sm font-medium text-white">{profile.username || profile.email}</p>
                    <p className="text-xs text-gray-400">{profile.email}</p>
                </div>
            )}

            {/* Account Links */}
            <div className="space-y-1">
                <Link
                    to="/app/profile"
                    onClick={onClose}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
                >
                    <FontAwesomeIcon icon={faUser} className="w-4 h-4 text-white" />
                    <span className="text-sm">User Profile</span>
                </Link>

                <Link
                    to="/app/billing"
                    onClick={onClose}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
                >
                    <FontAwesomeIcon icon={faCreditCard} className="w-4 h-4 text-white" />
                    <span className="text-sm">Billing & Subscription</span>
                </Link>

                <Link
                    to="/app/support"
                    onClick={onClose}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
                >
                    <FontAwesomeIcon icon={faLifeRing} className="w-4 h-4 text-white" />
                    <span className="text-sm">Support</span>
                </Link>
            </div>

            {/* Preferences */}
            <div className="pt-3 border-t border-gray-700">
                <button
                    onClick={toggleDarkMode}
                    className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon
                            icon={dark ? faMoon : faSun}
                            className="w-4 h-4 text-white"
                        />
                        <span className="text-sm">Dark Mode</span>
                    </div>
                    <div className={`w-8 h-5 rounded-full transition-colors ${dark ? 'bg-cyan-500' : 'bg-gray-600'} relative`}>
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${dark ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                    </div>
                </button>
            </div>

            {/* Logout */}
            <div className="pt-3 border-t border-gray-700">
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
                >
                    <FontAwesomeIcon icon={faSignOutAlt} className="w-4 h-4 text-white" />
                    <span className="text-sm">Logout</span>
                </button>
            </div>
        </div>
    );
};

export default ProfilePopOut;
